import React, { useState } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { createZendeskTicket } from '../services/api'

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
`

const ModalContent = styled.div`
  background: ${theme.color.background.light};
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.5);
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  border-bottom: 1px solid ${theme.color.border.light};
  background: ${theme.color.background.frame};
  border-radius: 12px 12px 0 0;
`

const ModalTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.color.text.base};
  margin: 0;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 2.4rem;
  color: ${theme.color.text.grey};
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${theme.color.text.base};
  }
`

const Form = styled.form`
  padding: 2rem;
`

const FieldContainer = styled.div`
  margin-bottom: 2rem;
`

const Label = styled.label`
  display: block;
  font-size: 1.4rem;
  font-weight: 600;
  color: ${theme.color.text.base};
  margin-bottom: 0.8rem;
`

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 1rem;
  font-size: 1.4rem;
  border: 1px solid ${props => props.hasError ? '#ff0000' : theme.color.border.light};
  border-radius: 4px;
  background: white;
  color: ${theme.color.text.base};

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ff0000' : theme.color.accent.primary};
  }

  &::placeholder {
    color: ${theme.color.text.grey};
  }
`

const EmailFieldGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  margin-bottom: 1rem;
`

const EmailInputWrapper = styled.div`
  flex: 1;
`

const IconButton = styled.button`
  background: ${theme.color.accent.primary};
  color: white;
  border: none;
  border-radius: 4px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 2rem;
  font-weight: 700;
  transition: all 0.2s;

  &:hover {
    background: #b8002d;
  }

  &:disabled {
    background: ${theme.color.text.grey};
    cursor: not-allowed;
    opacity: 0.6;
  }
`

const RemoveButton = styled(IconButton)`
  background: #dc3545;

  &:hover {
    background: #c82333;
  }
`

const ErrorText = styled.span`
  display: block;
  color: #ff0000;
  font-size: 1.2rem;
  margin-top: 0.5rem;
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 1rem 2rem;
  font-size: 1.4rem;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.variant === 'primary' ? `
    background: ${theme.color.accent.primary};
    color: white;
    border: none;

    &:hover:not(:disabled) {
      background: #b8002d;
    }

    &:disabled {
      background: ${theme.color.text.grey};
      cursor: not-allowed;
      opacity: 0.6;
    }
  ` : `
    background: transparent;
    color: ${theme.color.text.base};
    border: 1px solid ${theme.color.border.light};

    &:hover {
      background: ${theme.color.background.frame};
    }
  `}
`

const SuccessMessage = styled.div`
  background: #e8f5e9;
  color: #2e7d32;
  padding: 1.5rem;
  border-radius: 4px;
  margin-bottom: 2rem;
  font-size: 1.4rem;
`

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  padding: 1.5rem;
  border-radius: 4px;
  margin-bottom: 2rem;
  font-size: 1.4rem;
`

const InfoBox = styled.div`
  background: #e3f2fd;
  color: #1976d2;
  padding: 1.5rem;
  border-radius: 4px;
  margin-bottom: 2rem;
  font-size: 1.3rem;
  line-height: 1.6;
`

const WarningBox = styled.div`
  background: #fff3e0;
  color: #e65100;
  padding: 1.5rem;
  border-radius: 4px;
  margin-bottom: 2rem;
  font-size: 1.3rem;
  line-height: 1.6;
  border-left: 4px solid #ff9800;
`

interface AccountMergeModalProps {
  isOpen: boolean
  onClose: () => void
  userData: any
  currentUser?: any
}

const AccountMergeModal: React.FC<AccountMergeModalProps> = ({
  isOpen,
  onClose,
  userData,
  currentUser
}) => {
  const [emailsToMerge, setEmailsToMerge] = useState<string[]>([''])
  const [errors, setErrors] = useState<{ [key: number]: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState('')

  if (!isOpen) return null

  const currentEmail = userData?.user?.email || ''

  const updateEmail = (index: number, value: string) => {
    const newEmails = [...emailsToMerge]
    newEmails[index] = value
    setEmailsToMerge(newEmails)

    if (errors[index]) {
      const newErrors = { ...errors }
      delete newErrors[index]
      setErrors(newErrors)
    }
  }

  const addEmailField = () => {
    setEmailsToMerge([...emailsToMerge, ''])
  }

  const removeEmailField = (index: number) => {
    const newEmails = emailsToMerge.filter((_, i) => i !== index)
    setEmailsToMerge(newEmails)

    const newErrors = { ...errors }
    delete newErrors[index]
    setErrors(newErrors)
  }

  const validateForm = () => {
    const newErrors: { [key: number]: string } = {}

    emailsToMerge.forEach((email, index) => {
      if (!email.trim()) {
        newErrors[index] = 'Email address is required'
      } else if (!email.includes('@')) {
        newErrors[index] = 'Please enter a valid email address'
      } else if (email.toLowerCase() === currentEmail.toLowerCase()) {
        newErrors[index] = 'Cannot merge account with itself'
      }
    })

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setSubmitError('')
    setSubmitSuccess(false)

    try {
      const accountsList = emailsToMerge
        .filter(email => email.trim())
        .map(email => email.trim())
        .join(', ')

      // Get logged-in user's email
      const submitterEmail = currentUser?.email || ''

      // Add submitter email to description
      const description = `Please merge account(s) to remove: ${accountsList} into account to keep: ${currentEmail}\n\nRequest Submitted by: ${submitterEmail}`

      const requesterName = userData?.user?.first_name && userData?.user?.last_name
        ? `${userData.user.first_name} ${userData.user.last_name}`
        : 'Admin User'

      await createZendeskTicket({
        subject: 'Customer Account Merge',
        comment: description,
        requester_email: currentEmail,
        requester_name: requesterName,
        tags: ['account-merge', 'account_management', 'merging_multiple_accounts'],
        follower_email: submitterEmail
      })

      setSubmitSuccess(true)
      setEmailsToMerge([''])

      // Auto-close after 2 seconds
      setTimeout(() => {
        onClose()
        setSubmitSuccess(false)
      }, 2000)

    } catch (error) {
      console.error('Failed to submit account merge request:', error)
      setSubmitError(error instanceof Error ? error.message : 'Failed to submit account merge request')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      onClose()
      // Reset form state after closing
      setTimeout(() => {
        setEmailsToMerge([''])
        setErrors({})
        setSubmitSuccess(false)
        setSubmitError('')
      }, 300)
    }
  }

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Account Merge Request</ModalTitle>
          <CloseButton onClick={handleClose} disabled={isSubmitting}>×</CloseButton>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          {submitSuccess && (
            <SuccessMessage>
              Account merge request submitted successfully! Ticket has been created.
            </SuccessMessage>
          )}

          {submitError && (
            <ErrorMessage>{submitError}</ErrorMessage>
          )}

          <InfoBox>
            <strong>Current Account (Keep):</strong> {currentEmail}
            <br />
            Enter the email address(es) of the account(s) to be removed and merged into this account.
          </InfoBox>

          <WarningBox>
            <strong>Important:</strong>
            <br />
            <br />
            Please note that MarianaTek does not allow employee accounts to be merged. This form is only for clients and their account merge needs.
            <br />
            <br />
            Please inform the client merging an account is permanent and irreversible. The client will not be able to create accounts with merged email addresses in the future.
          </WarningBox>

          <FieldContainer>
            <Label>Enter the email address of the account to remove (will be merged into the kept account) *</Label>
            {emailsToMerge.map((email, index) => (
              <EmailFieldGroup key={index}>
                <EmailInputWrapper>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => updateEmail(index, e.target.value)}
                    placeholder="email@example.com"
                    hasError={!!errors[index]}
                    disabled={isSubmitting}
                  />
                  {errors[index] && <ErrorText>{errors[index]}</ErrorText>}
                </EmailInputWrapper>

                {index === emailsToMerge.length - 1 ? (
                  <IconButton
                    type="button"
                    onClick={addEmailField}
                    disabled={isSubmitting}
                    title="Add another email"
                  >
                    +
                  </IconButton>
                ) : (
                  <RemoveButton
                    type="button"
                    onClick={() => removeEmailField(index)}
                    disabled={isSubmitting}
                    title="Remove this email"
                  >
                    −
                  </RemoveButton>
                )}
              </EmailFieldGroup>
            ))}
          </FieldContainer>

          <ButtonContainer>
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Merge Request'}
            </Button>
          </ButtonContainer>
        </Form>
      </ModalContent>
    </ModalOverlay>
  )
}

export default AccountMergeModal
